import { useMutation, useQueryClient } from '@tanstack/react-query'
import { assignSchools } from '@/api/group/group.service'
import { reloadGroupsWithSchoolsAndTanks } from '@/utils/loadStorage'
import type { AssignSchoolsCommand } from '@/types/domain'
import { useGroupStore } from '@/store/group/useGroupStore'
import { useTankStore } from '@/store/tank/useTankStore'

interface AssignGroupParams {
  groupId: string
  tankIds: string[]  // tankIds will be converted to schoolIds
}

/**
 * Hook for assigning ungrouped tanks (schools) to a group
 *
 * Flow:
 * 1. Get schoolIds from tankIds via TankStore (tank.schoolId)
 * 2. Call assign-schools API
 * 3. Reload all group/tank data
 */
export const useAssignGroup = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ groupId, tankIds }: AssignGroupParams) => {
      // Get schoolIds from tankIds
      const tanks = useTankStore.getState().tanks
      const schoolIds = tankIds
        .map((tankId) => tanks[tankId]?.schoolId)
        .filter((id): id is string => id !== null && id !== undefined)

      if (schoolIds.length === 0) {
        throw new Error('No valid school IDs found for the selected tanks')
      }

      const command: AssignSchoolsCommand = {
        groupId,
        schoolIds,
        reason: 'Assigned via Assign Group modal',
      }

      await assignSchools(command)

      // Reload data after successful assignment
      await reloadGroupsWithSchoolsAndTanks()

      return { groupId, assignedCount: schoolIds.length }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] })
      queryClient.invalidateQueries({ queryKey: ['tanks'] })
    },
  })
}
